import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import agent from 'agent'
import * as actions from 'redux/actions/dashboard.actions'
import { getDashboardDataFailure, getDashboardDataSuccess } from 'redux/actions/dashboard.actions'
import {
  getDomainAveragePosition,
  getDomainOverview,
  getDomainPosition,
  getDomainSerpFeatureChart,
  getDomainTraffic,
  getDomainVisibility
} from './domain.sagas'
import { seoGuideApiToSectionsFormat } from 'helpers/seoGuideUtils'

import { getWebsiteAudit } from './websiteAudit.sagas'
import { exportSaga } from './export.sagas'

function* getDomains() {
  const domains = yield call(agent.Domains.all)
  return (domains.data || []).map(item => ({
    id: item.id,
    name: item.attributes.projectName,
    createdAt: item.attributes.createdAt,
    scheme: item.attributes.scheme,
    host: item.attributes.host,
    matchType: item.attributes.matchType,
    trackedCompetitors: item.attributes.trackedCompetitors
  }))
}

function* getSeoChecklist(domainId) {
  try {
    const body = yield call(agent.SeoChecklist.getAll, domainId)
    const checklist = seoGuideApiToSectionsFormat(body.data.attributes)
    yield put(
      getDashboardDataSuccess({
        key: 'seoChecklist',
        value: checklist
      })
    )
  } catch (error) {
    yield put(getDashboardDataFailure(error, { key: 'seoChecklist' }))
  }
}

function* getWebAudit(domainId, disabledFeatures = []) {
  try {
    let audits = null
    if (!disabledFeatures.includes('website-audit')) {
      audits = yield call(getWebsiteAudit, domainId)
    }
    yield put(
      getDashboardDataSuccess({
        key: 'websiteAudit',
        value: audits
      })
    )
  } catch (error) {
    yield put(getDashboardDataFailure(error, { key: 'websiteAudit' }))
  }
}

function* getNotifications(domainId) {
  try {
    const body = yield call(agent.Keywords.notifications, domainId)
    const notifications = body.data.map(item => {
      return (item?.attributes?.results?.data || []).map(subitem => ({
        keyword: subitem?.attributes?.word || '',
        position: {
          current: subitem?.attributes?.currentPosition || 0,
          previous: subitem?.attributes?.previousPosition || 0,
          change: subitem?.attributes?.positionChange || 0
        },
        traffic: subitem?.attributes?.traffic || 0,
        searchVolume: subitem?.attributes?.searchVolume || 0,
        device: subitem?.attributes?.device || null,
        searchEngine: subitem?.attributes?.searchEngine || null
      }))
    })
    yield put(
      getDashboardDataSuccess({
        key: 'notifications',
        value: notifications
      })
    )
  } catch (error) {
    yield put(getDashboardDataFailure(error, { key: 'notifications' }))
  }
}

function* getSeoSavings(domainId, disabledFeatures = []) {
  try {
    let savings = null
    if (!disabledFeatures.includes('savings')) {
      const [savingsChart] = yield all([call(agent.Domains.savingsChart, domainId)])
      savings = {
        chart: savingsChart.data.map(item => item.attributes)
      }
    }
    yield put(
      getDashboardDataSuccess({
        key: 'seoSavings',
        value: savings
      })
    )
  } catch (error) {
    yield put(getDashboardDataFailure(error, { key: 'seoSavings' }))
  }
}

function* getKeywordFindersHistory(disabledFeatures = []) {
  if (disabledFeatures.includes('keyword-finder')) return null
  const keywordFindersHistoryData = yield call(agent.Dashboard.keywordFinders)
  return keywordFindersHistoryData.data.map(item => ({
    id: item.id,
    name: item.attributes.word
  }))
}

function* getKeywordFindersLists(disabledFeatures = []) {
  if (disabledFeatures.includes('keyword-finder')) return null
  const keywordFindersListsData = yield call(agent.Dashboard.lists)
  return keywordFindersListsData.data.map(item => ({
    id: item.id,
    name: item.attributes.name
  }))
}

function* getKeywordFinder(disabledFeatures = []) {
  try {
    const [history, list] = yield all([
      call(getKeywordFindersHistory, disabledFeatures),
      call(getKeywordFindersLists, disabledFeatures)
    ])
    yield put(
      getDashboardDataSuccess({
        key: 'keywordFinder',
        value: {
          history,
          list
        }
      })
    )
  } catch (error) {
    yield put(getDashboardDataFailure(error, { key: 'keywordFinder' }))
  }
}

function* getReports() {
  try {
    const body = yield call(agent.Dashboard.reports)
    const reports = body.data
    yield put(
      getDashboardDataSuccess({
        key: 'reports',
        value: reports
      })
    )
  } catch (error) {
    yield put(getDashboardDataFailure(error, { key: 'reports' }))
  }
}
function* getOverview(domainId) {
  try {
    const body = yield call(getDomainOverview, domainId)
    const overview = body.attributes
    yield put(
      getDashboardDataSuccess({
        key: 'overview',
        value: overview
      })
    )
  } catch (error) {
    yield put(getDashboardDataFailure(error, { key: 'overview' }))
  }
}

function* getMovements(domainId) {
  try {
    const [
      visibilityChart,
      trafficChart,
      averageRankChart,
      serpFeatures,
      positionsChart
    ] = yield all([
      call(getDomainVisibility, domainId),
      call(getDomainTraffic, domainId),
      call(getDomainAveragePosition, domainId),
      call(getDomainSerpFeatureChart, domainId),
      call(getDomainPosition, domainId)
    ])
    yield put(
      getDashboardDataSuccess({
        key: 'movements',
        value: {
          visibilityChart,
          trafficChart,
          averageRankChart,
          serpFeatures,
          positionsChart
        }
      })
    )
  } catch (error) {
    yield put(getDashboardDataFailure(error, { key: 'movements' }))
  }
}

function* downloadReportSaga(params) {
  const { domainId, reportId, dateFrom, dateTo } = params.payload
  yield call(exportSaga, {
    params: { domainId, reportId, dateFrom, dateTo },
    type: 'report',
    request: agent.Reports.export
  })
}

function* getDashboardDataSaga(params) {
  const { domainId } = params.payload

  try {
    const state = yield select()
    const disabledFeatures = state.account?.disabledFeatures

    const domains = yield call(getDomains)
    const id = domainId ? domainId : domains.length ? domains[0].id : null
    yield put(actions.getDashboardDomainsSuccess(id, domains))
    if (id) {
      yield all([
        call(getWebAudit, id, disabledFeatures),
        call(getSeoSavings, id, disabledFeatures),
        call(getNotifications, id),
        call(getSeoChecklist, id),
        call(getKeywordFinder, disabledFeatures),
        call(getReports),
        call(getOverview, id),
        call(getMovements, id)
      ])
    }
  } catch (error) {
    yield put(getDashboardDataFailure(error))
  }
}

export default function* dashboardSagas() {
  yield all([
    takeLatest(actions.getDashboardData.type, getDashboardDataSaga),
    takeLatest(actions.downloadReport.type, downloadReportSaga)
  ])
}
